import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  CalendarDays,
  ChevronDown,
  Clock,
  Hash,
  ListChecks,
  Phone,
  Search,
  Users,
} from "lucide-react";
import {
  useCustomerTimeline,
  TIMELINE_PAGE_SIZE,
} from "../../../helpers/hooks/use-customer-timeline";
import type { CommunicationEvent } from "../../../types/crm";

interface Props {
  customerId: string;
}

type DateRangePreset = "all" | "30d" | "90d" | "6mo" | "12mo";
type SortOrder = "newest" | "oldest";

const DATE_RANGE_OPTIONS: Array<{ value: DateRangePreset; label: string }> = [
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "6mo", label: "Last 6 months" },
  { value: "12mo", label: "Last 12 months" },
  { value: "all", label: "All time" },
];

function dateRangeFloor(preset: DateRangePreset): number | null {
  const now = Date.now();
  switch (preset) {
    case "30d":
      return now - 30 * 86_400_000;
    case "90d":
      return now - 90 * 86_400_000;
    case "6mo":
      return now - 182 * 86_400_000;
    case "12mo":
      return now - 365 * 86_400_000;
    case "all":
    default:
      return null;
  }
}

/**
 * Top-level "Meetings" tab on the Customer 360 page. Reads
 * CommunicationEvent rows where `source='fireflies'`. Designed as a
 * scannable list of full-width cards rather than the compact timeline-row
 * format used by Communications — meetings are infrequent enough that
 * each one deserves real visual weight.
 *
 * Card composition (top to bottom):
 *   • Header: title, date, duration, "Open in Fireflies" link
 *   • Attendee chips (internal team has muted style, external is colored)
 *   • Overview paragraph
 *   • Action items (markdown — supports the `**Person**: action` format)
 *   • Keyword tags
 *
 * Source data: meeting metadata + searchableText emitted by
 * `FirefliesSnapshotService`. We parse the searchableText into sections
 * since OVERVIEW / ACTION ITEMS / KEYWORDS are deterministic markers
 * controlled by `formatMeetingSearchable()`.
 */
export function MeetingsSection({ customerId }: Props) {
  const [offset, setOffset] = useState(0);
  const [allEvents, setAllEvents] = useState<CommunicationEvent[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const lastProcessedKey = useRef<string | null>(null);

  // Filter / sort state — all client-side, since the timeline hook
  // already pulls every fireflies row for this customer.
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRangePreset>("90d");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");

  const { data, isLoading, isFetching, isError } = useCustomerTimeline(
    customerId,
    {
      source: "fireflies",
      offset,
    },
  );

  useEffect(() => {
    if (!data) return;
    const key = `${offset}`;
    if (lastProcessedKey.current === key) return;
    lastProcessedKey.current = key;

    const incoming = data.events ?? [];
    setAllEvents((prev) => {
      if (offset === 0) return incoming;
      const seen = new Set(prev.map((e) => e.id));
      return [...prev, ...incoming.filter((e) => !seen.has(e.id))];
    });
    setHasMore(incoming.length >= TIMELINE_PAGE_SIZE);
  }, [data, offset]);

  // Apply filter + sort to whatever's been fetched so far.
  const filteredEvents = useMemo(() => {
    const floor = dateRangeFloor(dateRange);
    const q = search.trim().toLowerCase();

    let out = allEvents;

    if (floor != null) {
      out = out.filter((e) => new Date(e.occurredAt).getTime() >= floor);
    }

    if (q) {
      out = out.filter((e) => {
        const meta = (e.metadata ?? {}) as MeetingMeta;
        const title = (meta.title ?? e.summary ?? "").toLowerCase();
        if (title.includes(q)) return true;
        const attendeeBlob = (meta.attendees ?? [])
          .map((a) => `${a.email} ${a.displayName ?? ""}`.toLowerCase())
          .join(" ");
        if (attendeeBlob.includes(q)) return true;
        const keywordBlob = (meta.keywords ?? []).join(" ").toLowerCase();
        if (keywordBlob.includes(q)) return true;
        return false;
      });
    }

    return [...out].sort((a, b) => {
      const da = new Date(a.occurredAt).getTime();
      const db = new Date(b.occurredAt).getTime();
      return sortOrder === "newest" ? db - da : da - db;
    });
  }, [allEvents, dateRange, search, sortOrder]);

  const filtersActive =
    search.trim().length > 0 || dateRange !== "all" || sortOrder !== "newest";

  return (
    <section className="bg-surface-primary rounded-lg border border-border-secondary shadow-sm">
      <header className="px-6 pt-5 pb-3 border-b border-border-secondary">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-content-tertiary" strokeWidth={1.75} />
            <h2 className="text-sm font-semibold text-content-primary">Meetings</h2>
            {allEvents.length > 0 && (
              <span className="text-[10px] uppercase tracking-wider text-content-tertiary">
                {filtersActive && filteredEvents.length !== allEvents.length
                  ? `${filteredEvents.length} of ${allEvents.length}`
                  : `${allEvents.length} captured`}
              </span>
            )}
          </div>

          {/* Filter / sort controls — visible only when there's data */}
          {allEvents.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-tertiary pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search title, attendee, topic…"
                  className="pl-7 pr-2 py-1 text-xs border border-border-secondary rounded-md w-56 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Date preset */}
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as DateRangePreset)}
                className="text-xs border border-border-secondary rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                aria-label="Date range"
              >
                {DATE_RANGE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              {/* Sort */}
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                className="text-xs border border-border-secondary rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                aria-label="Sort order"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
            </div>
          )}
        </div>

        <p className="mt-1 text-[11px] text-content-tertiary">
          Meeting transcripts &amp; summaries pulled nightly from Fireflies workspace
        </p>
      </header>

      {isError && (
        <div className="px-6 py-4 text-sm text-red-700">
          Failed to load meetings.
        </div>
      )}

      {!isError && allEvents.length === 0 && !isLoading && (
        <div className="px-6 py-10 text-center text-sm text-content-tertiary">
          No meetings recorded yet.
          <div className="mt-1 text-[11px]">
            New meetings appear after the daily 02:15 ET snapshot, or after
            the Fireflies backfill is run.
          </div>
        </div>
      )}

      {allEvents.length > 0 && filteredEvents.length === 0 && (
        <div className="px-6 py-10 text-center text-sm text-content-tertiary">
          No meetings match your filters.
          <div className="mt-2">
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setDateRange("all");
                setSortOrder("newest");
              }}
              className="text-[11px] font-medium text-blue-600 hover:text-blue-700"
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

      {filteredEvents.length > 0 && (
        <ul className="px-4 py-4 space-y-4">
          {filteredEvents.map((event, idx) => (
            <li key={event.id}>
              {/* First (most recent in current view) opens by default;
                  the rest start collapsed so the page is scannable. */}
              <MeetingCard event={event} defaultExpanded={idx === 0} />
            </li>
          ))}
        </ul>
      )}

      {hasMore && allEvents.length > 0 && (
        <div className="px-6 pb-4">
          <button
            type="button"
            onClick={() => setOffset((o) => o + TIMELINE_PAGE_SIZE)}
            disabled={isFetching}
            className="w-full text-xs font-medium text-content-secondary hover:text-content-primary py-2 border border-dashed border-border-secondary rounded-lg disabled:opacity-50"
          >
            {isFetching ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Single meeting card
// ---------------------------------------------------------------------------

interface MeetingMeta {
  title?: string;
  durationMinutes?: number;
  meetingLink?: string | null;
  attendees?: Array<{ email: string; displayName: string | null }>;
  participants?: string[];
  keywords?: string[];
}

function MeetingCard({
  event,
  defaultExpanded,
}: {
  event: CommunicationEvent;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const meta = (event.metadata ?? {}) as MeetingMeta;
  const sections = parseSections(event.searchableText ?? "");

  const title = meta.title ?? event.summary ?? "Untitled meeting";
  const duration = meta.durationMinutes ?? null;
  const attendees = (meta.attendees ?? []).filter((a) => a.email);
  const keywords = meta.keywords ?? [];

  return (
    <article className="bg-white rounded-lg border border-border-secondary hover:border-gray-300 transition-colors overflow-hidden">
      {/* ─── Header (always visible — clickable to toggle) ─── */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full p-5 text-left flex items-start justify-between gap-3 group"
      >
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-content-primary leading-snug truncate">
            {title}
          </h3>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-content-tertiary">
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="w-3 h-3" />
              {formatDateLong(event.occurredAt)}
            </span>
            {duration != null && duration > 0 && (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {Math.round(duration)} min
              </span>
            )}
            {!expanded && attendees.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <Users className="w-3 h-3" />
                {attendees.length}{" "}
                {attendees.length === 1 ? "attendee" : "attendees"}
              </span>
            )}
          </div>
        </div>
        <ChevronDown
          className={`shrink-0 w-4 h-4 mt-1 text-content-tertiary transition-transform ${
            expanded ? "rotate-180" : ""
          } group-hover:text-content-secondary`}
          strokeWidth={1.75}
        />
      </button>

      {expanded && (
        <div className="px-5 pb-5 -mt-2 space-y-4 border-t border-border-secondary pt-4 bg-gray-50/30">
          {/* ─── Attendees ─── */}
          {attendees.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-medium text-content-tertiary uppercase tracking-wider">
                <Users className="w-3 h-3" />
                Attendees
              </div>
              <div className="flex flex-wrap gap-1.5">
                {attendees.map((a) => {
                  const isInternal = isInternalEmail(a.email);
                  const display = a.displayName?.trim() || a.email;
                  return (
                    <span
                      key={a.email}
                      title={a.email}
                      className={
                        isInternal
                          ? "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-[11px]"
                          : "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium"
                      }
                    >
                      {display}
                      {isInternal && (
                        <span className="text-[9px] uppercase tracking-wider opacity-60">
                          Autumn
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Overview (Fireflies AI-generated, markdown rendered) ─── */}
          {sections.overview && (
            <div>
              <h4 className="text-[11px] font-medium text-content-tertiary uppercase tracking-wider mb-1.5">
                Overview
              </h4>
              <div className="prose prose-sm max-w-none prose-p:my-1.5 prose-p:leading-relaxed prose-strong:text-content-primary text-sm text-content-secondary">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {sections.overview}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* ─── Action items (Fireflies AI-generated, markdown rendered) ─── */}
          {sections.actionItems && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-medium text-content-tertiary uppercase tracking-wider">
                <ListChecks className="w-3 h-3" />
                Action items
              </div>
              <div className="prose prose-sm max-w-none prose-p:my-1 prose-strong:text-content-primary text-sm text-content-secondary">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {sections.actionItems}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* ─── Topics (keywords from Fireflies) ─── */}
          {keywords.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-medium text-content-tertiary uppercase tracking-wider">
                <Hash className="w-3 h-3" />
                Topics
              </div>
              <div className="flex flex-wrap gap-1.5">
                {keywords.map((k) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-[11px] font-medium"
                  >
                    <span className="opacity-60">#</span>
                    {k}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const INTERNAL_DOMAINS = ["autumnplatform.com", "autumly.com"];

function isInternalEmail(email: string): boolean {
  const e = email.toLowerCase();
  return INTERNAL_DOMAINS.some((d) => e.endsWith("@" + d));
}

function formatDateLong(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Parse the structured `searchableText` emitted by
 * `formatMeetingSearchable()`. Format is stable (we own the formatter):
 *
 *   <header line>
 *
 *   Attendees:
 *     - ...
 *
 *   OVERVIEW:
 *   ...
 *
 *   ACTION ITEMS:
 *   ...
 *
 *   KEYWORDS: a, b, c
 *
 *   LINK: url
 *
 * Returns each section's content (trimmed); missing sections come back null.
 */
interface ParsedSections {
  overview: string | null;
  actionItems: string | null;
}

function parseSections(text: string): ParsedSections {
  if (!text) return { overview: null, actionItems: null };
  return {
    overview: extractSection(text, "OVERVIEW:", [
      "ACTION ITEMS:",
      "KEYWORDS:",
      "LINK:",
    ]),
    actionItems: extractSection(text, "ACTION ITEMS:", ["KEYWORDS:", "LINK:"]),
  };
}

function extractSection(
  text: string,
  startMarker: string,
  endMarkers: string[],
): string | null {
  const start = text.indexOf(startMarker);
  if (start === -1) return null;
  let end = text.length;
  for (const marker of endMarkers) {
    const idx = text.indexOf(marker, start + startMarker.length);
    if (idx !== -1 && idx < end) end = idx;
  }
  const body = text.slice(start + startMarker.length, end).trim();
  return body || null;
}
